import { useEffect, useId, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import {
  updateOfferListKey,
  fetchOfferList,
  fetchPersonalisedOfferList,
  streamOfferList,
  getMinOffers,
} from 'actions/OfferActions'
import arrayShuffle from 'lib/array/arrayShuffle'
import memoize from 'lib/memoize/memoize'
import getOfferListKey from 'lib/offer/offerListKey'
import { without } from 'lib/array/arrayUtils'
import { SetOptional } from 'type-fest'
import { useSearchAnalytics } from 'hooks/Analytics/useSearchAnalytics'
import {
  Filter_1_0_0, Search_3_0_0,
  SearchContext_1_0_3,
  Sort_1_0_0,
} from '@luxuryescapes/contract-data-event-schemas/lib/com.luxgroup'
import { omitKeys } from '../../lib/object/objectUtils'

export interface Options {
  /**
   * For when the offer list is no longer needed, but still needs to be called to avoid violating the Rules of Hooks
   * */
  disabled?: boolean
  /**
   * Randomize the offer ids in the list
   * Will be stable for that set of offer ids
   */
  randomize?: boolean
  /**
   * An array of offer ids to explicitly exclude from the results
   */
  excludedIds?: Array<string>
  /**
   * Whether explicitly filter out sold-out offer ids from the list
   * This will use the meta-data to determine if they are sold out
   */
  filterSoldOut?: boolean
  /**
   * Whether check the availability of the offer on the client side
   */
  clientCheckAvailability?: boolean

  minRequiredOffers?: number
  searchId?: string
  includeAllBedbankWithSales?: boolean
  evVersion?: 'current' | 'version_2'
}

export interface TrackingOptions {
  /**
   * Whether to disable tracking for this search
   */
  disable?: boolean
  searchEvent: Search_3_0_0
  /**
   * The search state that were used to generate the this search
   */
  searchContext: SearchContext_1_0_3
  filterContexts?: Array<Filter_1_0_0>
  sortContext?: Sort_1_0_0
  searchSessionId?: string
  ignoreChangedAttributes?: Array<keyof App.OfferListFilters>
}

/**
 * Memoize is used here as it provides a stable set of offer ids for that exact version of the array
 * This ensures that we always return the same randomised set throughout the life of the page
 */
const getRandomizedOfferIds = memoize((offerIds: Array<string>) => arrayShuffle(offerIds))

const emptyOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: true,
  error: null,
}

const emptyDisabledOfferList: SetOptional<App.OfferList, 'key'> = {
  offerIds: [],
  fetching: false,
  error: null,
}

function useOfferList(
  filters: App.OfferListFilters | undefined,
  options: Options = {},
  trackingOptions?: TrackingOptions,
): App.OfferList {
  const {
    randomize,
    excludedIds,
    filterSoldOut,
    clientCheckAvailability,
    minRequiredOffers,
    searchId,
    evVersion
  } = options

  const streamResults = filters?.isStream
  const listenerId = useId()
  const searchDisabled = options.disabled || !filters
  const dispatch = useAppDispatch()
  const { trackSearchEvent } = useSearchAnalytics({
    searchEvent: trackingOptions?.searchEvent,
    searchContext: trackingOptions?.searchContext,
    filterContexts: trackingOptions?.filterContexts,
    sortContext: trackingOptions?.sortContext,
    searchSessionId: trackingOptions?.searchSessionId,
  })

  const { listKey, trackedListKey } = useMemo(() => {
    if (filters) {
      return {
        listKey: getOfferListKey(filters),
        trackedListKey: getOfferListKey(omitKeys(trackingOptions?.ignoreChangedAttributes ?? [], filters)),
      }
    }
    return {
      listKey: '',
      trackedListKey: '',
    }
  }, [filters, trackingOptions?.ignoreChangedAttributes])

  const [tracked, setTracked] = useState<Set<string>>(new Set())
  useEffect(() => {
    if (searchDisabled) return

    let timeoutId: NodeJS.Timeout | undefined
    if (!trackingOptions?.disable && trackingOptions?.searchContext && !tracked.has(trackedListKey)) {
      timeoutId = setTimeout(() => {
        trackSearchEvent()
        setTracked(prev => new Set([...prev, trackedListKey]))
      }, 100)
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
    // eslint-disable-next-line
  }, [trackedListKey, searchDisabled, trackingOptions?.disable])

  // Fetch offer list
  useEffect(() => {
    if (searchDisabled) return

    if (streamResults) {
      dispatch(streamOfferList(filters, listenerId, minRequiredOffers, searchId, { evVersion }))
    } else if (filters.lere) {
      dispatch(fetchPersonalisedOfferList(filters))
    } else {
      dispatch(fetchOfferList(filters, {
        clientCheckAvailability,
      }))
    }
    // eslint-disable-next-line
  }, [listKey, searchDisabled])

  useEffect(() => {
    return () => {
      if (streamResults && !searchDisabled) {
        dispatch(updateOfferListKey({ offerListKey: listKey, listenerId }))
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey])

  const offerList = useAppSelector((state) => {
    if (searchDisabled) {
      return emptyDisabledOfferList
    }
    return state.offer.offerLists[listKey] ?? emptyOfferList
  })

  const offerListFilters = useAppSelector(state => state.offer.offerListFilterOptions[listKey])

  useEffect(() => {
    if (minRequiredOffers && minRequiredOffers > 0) {
      dispatch(getMinOffers({ offerListKey: listKey, minOffers: minRequiredOffers }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minRequiredOffers, offerList.fetching, offerListFilters])

  const metaData = useAppSelector(state => state.offer.searchResultMetadata.offerMetaData[listKey])

  const finalOfferList = useMemo(() => {
    let postExclusionIds = excludedIds ? without(offerList.offerIds, ...excludedIds) : offerList.offerIds

    if (filterSoldOut && metaData) {
      postExclusionIds = postExclusionIds.filter(id => {
        const offerData = metaData[id]
        // if we don't have any meta data on it, assume it's available
        return !offerData || offerData.available
      })
    }

    if (randomize) {
      return {
        ...offerList,
        key: offerList.key ?? listKey,
        offerIds: getRandomizedOfferIds(postExclusionIds),
      }
    }
    return {
      ...offerList,
      key: offerList.key ?? listKey,
      offerIds: postExclusionIds,
    }
  }, [excludedIds, offerList, filterSoldOut, metaData, randomize, listKey])
  return finalOfferList
}

export default useOfferList
